import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Undo2, Loader2, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

const SECTION_ORDER = ["Revenue", "Direct", "Indirect", "Overhead", "Other"];

const fmtTs = (ts) =>
  new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function HistoryLogDialog({ open, onClose, onReverted }) {
  const [entries, setEntries] = useState(null);
  const [undoing, setUndoing] = useState(null);

  const fetchLog = useCallback(() => {
    api.getForecastChangelog().then(setEntries).catch(() => toast.error("Failed to load change history"));
  }, []);

  useEffect(() => {
    if (open) {
      setEntries(null);
      fetchLog();
    }
  }, [open, fetchLog]);

  const doUndo = async (id) => {
    setUndoing(id);
    try {
      await api.undoForecastChange(id);
      toast.success("Change undone");
      fetchLog();
      onReverted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    } finally {
      setUndoing(null);
    }
  };

  const groups = SECTION_ORDER
    .map((s) => [s, (entries || []).filter((e) => e.section === s)])
    .filter(([, list]) => list.length > 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="history-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-sky-600" />
            Adjustment History
          </DialogTitle>
          <DialogDescription className="text-xs">
            Every saved change to the forecast scenario — who, when, and what changed. Undo restores the previous value.
          </DialogDescription>
        </DialogHeader>
        {entries == null ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : entries.length === 0 ? (
          <p className="py-6 text-center text-xs text-slate-400" data-testid="history-log-empty">
            No adjustments logged yet — changes are recorded every time the scenario saves
          </p>
        ) : (
          <div className="space-y-4">
            {groups.map(([section, list]) => (
              <div key={section}>
                <h4
                  className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5"
                  data-testid={`history-section-${section.toLowerCase()}`}
                >
                  {section} · {list.length}
                </h4>
                <div className="space-y-1">
                  {list.map((e) => (
                    <div
                      key={e.id}
                      className={cn(
                        "flex items-center gap-2 rounded border border-slate-100 px-2.5 py-1.5",
                        e.undone && "opacity-50"
                      )}
                      data-testid={`history-entry-${e.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-700 truncate" title={e.label}>{e.label}</p>
                        <p className="text-[10px] text-slate-400">
                          {e.user} · {fmtTs(e.ts)}
                          {e.undone && e.undone_by && ` · undone by ${e.undone_by} ${fmtTs(e.undone_at)}`}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-[11px] gap-1 shrink-0"
                        disabled={e.undone || undoing === e.id}
                        onClick={() => doUndo(e.id)}
                        data-testid={`history-undo-${e.id}`}
                      >
                        {undoing === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
                        {e.undone ? "Undone" : "Undo"}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
